import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { mdiDownload } from '@mdi/js';
import { Box } from '@mui/material';

import {
    ITrainingFragment,
    ITrainingUserEdgeFragment,
    ITrainingRole,
    IFeature,
    ExportTrainingParticipantsDocument,
    CurrentUserDocument,
} from 'graphql/types';
import { TTableAction } from 'common/types';
import { useFrontendPermissions } from 'user/hooks';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { BoxLoader } from 'common/components/Loader';
import { TrainingParticipantTable } from 'training/components/TrainingParticipantTable';
import { TrainingParticipantMatrix } from 'training/components/TrainingParticipantMatrix';
import { FilterBar } from 'common/components/FilterBar';
import { parseJSON } from 'common/utils/json';
import { ITableDataProps } from 'training/components/Section/Participants';
import { InlineStickyWrapper } from 'common/components/InlineStickyWrapper';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { useFeature } from 'common/hooks/useFeature';
import { EView, ViewToggle } from 'common/components/ViewToggle';

interface IProps {
    isEditable?: boolean;
    loading?: boolean;
    participants?: ITrainingUserEdgeFragment[];
    tableActions?: TTableAction[];
    tableDataProps: ITableDataProps;
    training: ITrainingFragment;
}

export const TrainingParticipantView = ({
    isEditable,
    loading,
    participants,
    tableActions,
    tableDataProps,
    training,
}: IProps) => {
    const [translate] = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const routeQuery = useRouteQuery();
    const { canUse: canExport } = useFeature(IFeature.Export);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportReport, { loading: loadingExport }] = useMutation(
        ExportTrainingParticipantsDocument
    );
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const currentUserEmail = currentUserData?.currentUser?.email;

    const { canUpdate: canUpdateTraining } = useFrontendPermissions('training');

    const localStorageView = localStorage.getItem('trainingParticipantsView');
    const currentRouteView = routeQuery.get('view');
    const parsedLocalStorageView =
        !!localStorageView && parseJSON(localStorageView);
    const viewParam = parsedLocalStorageView || currentRouteView;

    const { paginationProps, searchProps, selectionProps } = tableDataProps;
    const { setPage } = paginationProps || {};
    const {
        filters,
        filterValues,
        filterValuesForQuery,
        searchQueryParam,
        onFilterChange,
        onSearch,
    } = searchProps;
    const { selected, onSelectAll } = selectionProps || {};

    const isGroupManager = !!training.rolesForCurrentUser?.includes(
        ITrainingRole.Groupmanager
    );
    const isTrainer = !!training.rolesForCurrentUser?.includes(
        ITrainingRole.Trainer
    );
    const isMentor = !!training.rolesForCurrentUser?.includes(
        ITrainingRole.Mentor
    );

    const canManageTraining = isTrainer || isMentor || canUpdateTraining;
    const canViewMatrix = isGroupManager || canManageTraining;

    const navigateToView = (view: EView) => {
        const newSearchParams = new URLSearchParams(window.location.search);

        if (newSearchParams.get('view') === view) return;

        newSearchParams.set('view', view);

        navigate(
            { ...location, search: `?${newSearchParams.toString()}` },
            { replace: true }
        );
    };

    const setLocalStorageView = (view: EView) => {
        const newLocalStorageView = view;

        localStorage.setItem(
            'trainingParticipantsView',
            JSON.stringify(newLocalStorageView)
        );

        navigateToView(view);
    };

    useEffect(() => {
        if (!!localStorageView && !currentRouteView) {
            const currentView = parsedLocalStorageView || EView.Matrix;

            navigateToView(currentView);
        }
    }, [localStorageView, currentRouteView]);

    const handleExport = async () => {
        try {
            const exportResults = await exportReport({
                variables: {
                    trainingId: training.id,
                    ...filterValuesForQuery,
                },
            });
            const fileUrl =
                exportResults.data?.exportTrainingParticipants?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(
                    translate('trainingParticipants.exportSuccess'),
                    {
                        variant: 'success',
                    }
                );
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    const currentView =
        !canViewMatrix || !viewParam || viewParam === EView.List
            ? EView.List
            : EView.Matrix;

    const viewToggle = canViewMatrix ? (
        <ViewToggle
            buttons={[EView.List, EView.Matrix]}
            value={currentView}
            onClickButton={(key) => {
                if (key === EView.Matrix) {
                    setLocalStorageView(EView.Matrix);

                    return;
                }

                setLocalStorageView(EView.List);
            }}
        />
    ) : null;

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);

        if (!!selected?.length) onSelectAll?.();
    };

    return (
        <Box>
            <InlineStickyWrapper fullWidth leftOffset={3}>
                <FilterBar
                    useFilterDrawer
                    barAppend={
                        (canManageTraining || isGroupManager) &&
                        canExport && (
                            <Button
                                aria-label={translate('export')}
                                disabled={!participants?.length}
                                startIcon={
                                    <Icon path={mdiDownload} size="1.9rem" />
                                }
                                sx={{ flex: 1 }}
                                variant="outlined"
                                onClick={() => setShowExportDialog(true)}
                            />
                        )
                    }
                    barPrepend={viewToggle}
                    disabled={loading}
                    filterDrawerProps={{
                        title: translate(
                            'filterForm.trainingParticipants.title'
                        ),
                        description: translate(
                            'filterForm.trainingParticipants.description'
                        ),
                    }}
                    formFilters={filters}
                    formFilterValues={filterValues}
                    initialSearchValue={searchQueryParam}
                    placeholder={translate('filterBarPlaceholder')}
                    onFiltersChange={(values) => {
                        setPage?.(1);
                        if (!!selected?.length) onSelectAll?.();
                        onFilterChange?.(values);
                    }}
                    onSearch={handleSearch}
                    onSearchClear={() => handleSearch('')}
                />
            </InlineStickyWrapper>

            {loading ? (
                <BoxLoader />
            ) : currentView === EView.List || !canViewMatrix ? (
                <TrainingParticipantTable
                    canManageTraining={canManageTraining}
                    isEditable={isEditable}
                    isGroupManager={isGroupManager}
                    paginationProps={paginationProps}
                    participants={participants}
                    selectionProps={selectionProps}
                    tableActions={tableActions}
                    training={training}
                />
            ) : (
                <TrainingParticipantMatrix
                    canManageTraining={canManageTraining}
                    isGroupManager={isGroupManager}
                    paginationProps={paginationProps}
                    participants={participants}
                    training={training}
                />
            )}

            <ConfirmDialog
                confirmText={translate('sendExport')}
                loading={loadingExport}
                open={showExportDialog}
                title={translate(`trainingParticipants.exportDialog.title`)}
                onCancel={() => setShowExportDialog(false)}
                onClose={() => setShowExportDialog(false)}
                onConfirm={handleExport}
            >
                {translate(`trainingParticipants.exportDialog.text`, {
                    email: currentUserEmail,
                })}
            </ConfirmDialog>
        </Box>
    );
};
